import {
  CheckCircleTwoTone,
  LocalShippingTwoTone,
  PersonTwoTone,
  ReceiptTwoTone,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useEffect } from 'react';
import { getI18n, useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { OnlineTourCard } from '../components/OnlineTourCard';
import { OrderSummary } from '../components/OrderSummary';
import PageHeader from '../components/PageHeader';
import { useTourSearchDetail } from '../hooks/api/tour-search';
import { IOnlineTourSearchResult, ISalesOrderResult } from '../utils/types';
import { sendToNative } from '../utils';

export function OrderResultPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const pnr = params.get('pnr');
  const location = useLocation();
  const orders = location.state as ISalesOrderResult[];
  const locale = getI18n().language;

  return (
    <Box>
      <PageHeader
        title={
          <>
            <CheckCircleTwoTone color="success" /> {t('orderResultPage.title')}
          </>
        }
      />

      <Typography>{t('orderResultPage.subtitle', { pnr })}</Typography>

      <Box sx={{ mt: 2 }}>
        {!orders || !orders[0] ? (
          <Alert severity="error">{t('orderResultPage.orderNotFound')}</Alert>
        ) : (
          <>
            <TourCard tourId={orders[0].salesGroup.tour.id} />
            <Grid2 container sx={{ mt: 2 }} spacing={2}>
              {orders.map((order) => (
                <Grid2 xs={12} md={6} key={order.id}>
                  <Paper>
                    <ListSubheader sx={{ lineHeight: 2, marginTop: 1 }}>
                      {order.customer.lastName}, {order.customer.firstName}
                    </ListSubheader>
                    <OrderSummary
                      pnr={order.salesGroup.code}
                      orderNumber={order.order.documentNumber || ''}
                      orderDate={new Date(order.order.createdDate)}
                      bedType={order.bedType.name || ''}
                    />
                  </Paper>
                  <Paper sx={{ mt: 2 }}>
                    <Box>
                      <ListSubheader sx={{ lineHeight: 2, marginTop: 1 }}>
                        {t('orderResult.accepters')}
                      </ListSubheader>
                      <List>
                        {order.salesOrderAccepters.map((acc) => (
                          <Tooltip title={acc.role.name} key={acc.id}>
                            <ListItem>
                              <ListItemIcon>
                                {
                                  {
                                    SPP: <PersonTwoTone />,
                                    FAA: <ReceiptTwoTone />,
                                    MTA: <LocalShippingTwoTone />,
                                  }[acc.role.code]
                                }
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  acc.party.partyInfo.taxNo
                                    ? `${acc.party.partyInfo.name} (VKN: ${acc.party.partyInfo.taxNo})`
                                    : `${acc.party.partyInfo.lastName}, ${acc.party.partyInfo.firstName}`
                                }
                                secondary={
                                  <>
                                    {acc.address.fullAddress}
                                    <br />
                                    {acc.electronicAddress.electronicAddress}
                                    <br />
                                    {acc.phoneNumber.fullNumber}
                                  </>
                                }
                              />
                            </ListItem>
                          </Tooltip>
                        ))}
                      </List>
                    </Box>
                  </Paper>
                </Grid2>
              ))}
            </Grid2>
          </>
        )}
      </Box>

      <Paper sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('orderResultPage.guest')}</TableCell>
              <TableCell>{t('orderResultPage.price')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <TableRow key={order.id}>
                <TableCell>
                  {order.customer.lastName}, {order.customer.firstName}
                  <br />
                  <Typography variant="caption">
                    {order.bedType.name}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {order.price?.balance
                    ? order.price.balance.toLocaleString(locale, {
                        style: 'currency',
                        currency: order.price?.currency.code || 'USD',
                      })
                    : ''}
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ fontWeight: 'bold' }}>
              <TableCell>{t('orderResultPage.total')}</TableCell>
              <TableCell>
                {orders
                  .reduce((acc, order) => acc + (order.price?.balance || 0), 0)
                  .toLocaleString(locale, {
                    style: 'currency',
                    currency: orders[0].price?.currency.code || 'USD',
                  })}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>

      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={1}
        sx={{ my: 2 }}
      >
        <Button
          color="inherit"
          onClick={() => {
            if (window.Android) {
              sendToNative('print', null);
            } else {
              window.print();
            }
          }}
        >
          {t('orderResultPage.print')}
        </Button>
        <Link
          to={`/redirect-balances?pnr=${orders[0].salesGroup.code}&lastname=${orders[0].customer.lastName}`}
        >
          <Button variant="contained" color="primary">
            {t('orderResultPage.payment')}
          </Button>
        </Link>
      </Stack>
    </Box>
  );
}

function TourCard(props: {
  tourId: number;
  onLoad?: (data?: IOnlineTourSearchResult) => void;
}) {
  const { data, isLoading } = useTourSearchDetail({ id: props.tourId });

  useEffect(() => {
    props.onLoad && props.onLoad(data);
  }, [data, props]);

  if (!data) return null;
  if (isLoading) return <LinearProgress />;

  return (
    <OnlineTourCard
      tourId={data.id}
      tourName={data.name}
      tourCode={data.code}
      start={{
        departureCity: data.departureDepartureCity,
        departureAirport: data.departureDeparture,
        arrivalCity: data.arrivalDepartureCity,
        arrivalAirport: data.arrivalDeparture,
      }}
      end={{
        departureCity: data.arrivalDepartureCity,
        departureAirport: data.arrivalDeparture,
        arrivalCity: data.arrivalArrivalCity,
        arrivalAirport: data.arrivalArrival,
      }}
      tourStartDate={data.departureDate}
      tourFinishDate={data.arrivalDate}
      night={data.dayCount - 1}
      day={data.dayCount}
      currencyCode={data.currencyCode}
      remainingTime={null}
      remainingSeat={null}
      coverImg={data.images[0]?.path}
      disableActions
      hideShareAndPrint
    />
  );
}
